import { useAppDispatch, useAppSelector } from 'app/hooks';
import DeliveryProgressModal from 'components/common/DeliveryProgressModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { ToastSample } from 'components/common/Toast/models/toast';
import ToastComponent from 'components/common/Toast/page';
import { workerActions } from 'features/common/workerSlice';
import deliveryService from 'features/Delivery/deliveryService';
import {
  deliveryListActions,
  selectAsyncCreationShowCompletionToast,
  selectAsyncCreationTask,
  selectAsyncCreationTaskId,
  selectAsyncCreationTaskStatus,
} from 'features/Delivery/deliverySlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ASYNC_TASK_POLL_INTERVAL_MS,
  ASYNC_TASK_TIMEOUT_MS,
  STATUS_RESPONSE_COMPLETED,
  STATUS_RESPONSE_FAILED,
  STATUS_RESPONSE_SUCCESS,
} from '../../constants';

interface AsyncDeliveryStatusCheckerProps {
  onDeliveryCompleted?: () => void;
}

/**
 * Component to check async delivery creation status on page load
 * - Shows popup if task is still in progress
 * - Shows toast notification if task completed/failed and page was reloaded
 */
const AsyncDeliveryStatusChecker = ({ onDeliveryCompleted }: AsyncDeliveryStatusCheckerProps) => {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  const asyncTask = useAppSelector(selectAsyncCreationTask);
  const taskId = useAppSelector(selectAsyncCreationTaskId);
  const taskStatus = useAppSelector(selectAsyncCreationTaskStatus);
  const showCompletionToast = useAppSelector(selectAsyncCreationShowCompletionToast);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartRef = useRef<number | null>(null);
  const [hasShownToast, setHasShownToast] = useState(false);
  const [toastList, setToastList] = useState<any[]>([]);
  const hasShownPopupRef = useRef(false);
  const hasStartedPollingRef = useRef(false);

  // Check task status function
  const checkTaskStatus = useCallback(
    async (currentTaskId: string) => {
      try {
        // Client-side timeout: stop polling if exceeded
        if (pollingStartRef.current && Date.now() - pollingStartRef.current > ASYNC_TASK_TIMEOUT_MS) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          closeGlobalModal();
          dispatch(workerActions.resetWorker());
          dispatch(
            deliveryListActions.setAsyncCreationTaskFailed({
              error: t('DeliveryFeature.Text.task_timeout') || 'タスクがタイムアウトしました。',
              showToast: true,
            })
          );
          return;
        }

        const response = await deliveryService.getAsyncDeliveryCreateStatus(currentTaskId);
        if (response.status === STATUS_RESPONSE_SUCCESS && response.data) {
          const { status } = response.data;

          // Update progress
          if (status === 'progressing') {
            dispatch(
              deliveryListActions.setAsyncCreationTaskProgressing({
                progress: response.data.progress || 'Processing...',
              })
            );
          }

          // Check if task is completed or failed
          if (status === STATUS_RESPONSE_FAILED || status === STATUS_RESPONSE_COMPLETED) {
            dispatch(workerActions.resetWorker());
            // Clear the interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }

            // Close the modal
            closeGlobalModal();

            if (status === STATUS_RESPONSE_COMPLETED) {
              // Mark as completed with toast flag
              dispatch(deliveryListActions.setAsyncCreationTaskCompleted({ showToast: true }));

              // Trigger callback to reload deliveries
              if (onDeliveryCompleted) {
                onDeliveryCompleted();
              }
            } else if (status === STATUS_RESPONSE_FAILED) {
              const msgError = response.data.error || t('App.error');
              // Mark as failed with toast flag
              dispatch(
                deliveryListActions.setAsyncCreationTaskFailed({
                  error: msgError || 'Unknown error',
                  showToast: true,
                })
              );
            }
          }
        }
      } catch (error) {
        console.error('Failed to check async delivery creation status:', error);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        closeGlobalModal();
        dispatch(workerActions.resetWorker());
        dispatch(
          deliveryListActions.setAsyncCreationTaskFailed({
            error: t('DeliveryFeature.Text.task_status_check_failed') || 'タスクの状態確認に失敗しました。',
            showToast: true,
          })
        );
      }
    },
    [dispatch, t, onDeliveryCompleted, closeGlobalModal]
  );

  // On mount: Check if there's an ongoing task (run once)
  useEffect(() => {
    // Only run once on mount
    if (hasStartedPollingRef.current) {
      return;
    }

    if (taskId && taskStatus && taskStatus !== 'idle') {
      // If task is in progress, show popup and start polling
      if (taskStatus === 'started' || taskStatus === 'progressing') {
        // Check if persisted task is stale (older than timeout)
        const taskStartTime = asyncTask.startTime;
        if (taskStartTime && Date.now() - new Date(taskStartTime).getTime() > ASYNC_TASK_TIMEOUT_MS) {
          dispatch(workerActions.resetWorker());
          dispatch(
            deliveryListActions.setAsyncCreationTaskFailed({
              error: t('DeliveryFeature.Text.task_timeout') || 'タスクがタイムアウトしました。',
              showToast: true,
            })
          );
          return;
        }
        hasStartedPollingRef.current = true;

        // Only show popup once using ref to prevent infinite loop
        if (!hasShownPopupRef.current) {
          hasShownPopupRef.current = true;
          showGlobalModal(() => (
            <DeliveryProgressModal
              title={t('App.info')}
              message={
                t('DeliveryFeature.Text.delivery_creation_in_progress') ||
                'Delivery creation is in progress. Please wait...'
              }
              isProcessing={true}
            />
          ));
        }

        // Start polling only if not already started
        if (!intervalRef.current) {
          pollingStartRef.current = Date.now();
          intervalRef.current = setInterval(() => {
            checkTaskStatus(taskId);
          }, ASYNC_TASK_POLL_INTERVAL_MS);
        }
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Separate effect: Handle task completion/failure cleanup
  useEffect(() => {
    if (!taskId || !taskStatus) {
      return;
    }

    if (taskStatus === 'completed' || taskStatus === 'failed') {
      // Stop polling when task finishes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Reset refs for next time
      hasShownPopupRef.current = false;
      hasStartedPollingRef.current = false;
    }
  }, [taskId, taskStatus, closeGlobalModal]);

  // Show toast notification when task completes/fails (only once)
  useEffect(() => {
    if (showCompletionToast && !hasShownToast) {
      if (taskStatus === 'completed') {
        // Show success toast
        const toast = ToastSample.toastSuccess(
          t('App.success'),
          t('DeliveryFeature.Text.delivery_created_successfully') || 'Delivery created successfully!'
        );
        setToastList([toast]);
        setHasShownToast(true);

        // Clear toast flag and reset task after showing
        setTimeout(() => {
          dispatch(deliveryListActions.clearCompletionToastFlag());
          dispatch(deliveryListActions.resetAsyncCreationTask());
        }, 5000);
      } else if (taskStatus === 'failed') {
        // Show error toast
        const errorMessage = asyncTask.error || t('App.error') || 'Unknown error';
        const toast = ToastSample.toastError(t('App.error'), errorMessage);
        setToastList([toast]);
        setHasShownToast(true);

        // Clear toast flag and reset task after showing
        setTimeout(() => {
          dispatch(deliveryListActions.clearCompletionToastFlag());
          dispatch(deliveryListActions.resetAsyncCreationTask());
        }, 5000);
      }
    }
  }, [showCompletionToast, taskStatus, hasShownToast, asyncTask.error, dispatch, t]);

  // Render toast if needed
  if (toastList.length > 0) {
    return <ToastComponent toastList={toastList} position={'bottom-end'} autoDelete={true} autoDeleteTime={5000} />;
  }

  return null;
};

export default AsyncDeliveryStatusChecker;
